import { useCallback, useEffect, useState } from 'react';
import type { ChatTab, UserRole } from '@/types';
import { getQuickActionsFor, type QuickActionCard } from '@/config/static/workspace';
import { PinIcon, QuickActionSparkIcon } from '@/components/icons';

interface QuickActionCardsProps {
  role: UserRole;
  /** Matches the active composer tab (Users, Devices, Credentials, Events). */
  category: ChatTab;
  onSelect: (text: string) => void;
}

export function QuickActionCards({ role, category, onSelect }: QuickActionCardsProps) {
  const [cards, setCards] = useState<QuickActionCard[]>(() => getQuickActionsFor(role, category));

  useEffect(() => {
    setCards(getQuickActionsFor(role, category));
  }, [role, category]);

  const togglePin = useCallback((id: string) => {
    setCards((prev) => prev.map((c) => (c.id === id ? { ...c, pinned: !c.pinned } : c)));
  }, []);

  return (
    <section className="quick-actions">
      {cards.map((card) => {
        const pinned = Boolean(card.pinned);
        return (
          <div
            key={card.id}
            role="button"
            tabIndex={0}
            className={`quick-actions__card${pinned ? ' quick-actions__card--pinned' : ''}`}
            onClick={() => onSelect(card.text)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelect(card.text);
              }
            }}
          >
            <button
              type="button"
              className={`quick-actions__pin${pinned ? ' quick-actions__pin--pinned' : ''}`}
              title={pinned ? 'Unpin' : 'Pin'}
              aria-label={pinned ? 'Unpin quick action' : 'Pin quick action'}
              aria-pressed={pinned}
              onClick={(e) => {
                e.stopPropagation();
                togglePin(card.id);
              }}
            >
              <PinIcon filled={pinned} />
            </button>
            <QuickActionSparkIcon size={18} className="quick-actions__sparkle-icon" />
            <span className="quick-actions__text">{card.text}</span>
          </div>
        );
      })}
    </section>
  );
}
